import { scales } from 'chart.js';

export function calculateDistanceBetweenCoords(coords) {
  if (coords.length < 2) return 0;
  const xDistance = coords[coords.length - 1].x - coords[0].x;
  const yDistance = coords[coords.length - 1].y - coords[0].y;
  return Math.sqrt(xDistance * xDistance + yDistance * yDistance);
}

export function addArchToNade(cords, completionPercentage, distance) {
  const multiplierBase = -2 * completionPercentage ** 2 + 2 * completionPercentage;
  const scaledDistance = distance / 700;
  const multiplier = multiplierBase * scaledDistance;
  cords.x -= (150 * multiplier);
  return cords;
}

export function getProjectileColor(grenadeType) {
  const colors = {
    hegrenade: '#ff6b6b',
    flashbang: '#ffd93d',
    smokegrenade: '#6bcf7f',
    molotov: '#ff8c42',
    incgrenade: '#ff8c42',
    decoy: '#95a5a6',
  };
  return colors[grenadeType?.toLowerCase()] || '#ffffff';
}

export function getGrenadeIcon(grenadeType) {
  if (!grenadeType) return null;

  const typeLower = grenadeType.toLowerCase();
  const icons = {
    weapon_hegrenade: '/img/icons/weapon_hegrenade.png',
    hegrenade: '/img/icons/weapon_hegrenade.png',
    weapon_flashbang: '/img/icons/weapon_flashbang.png',
    flashbang: '/img/icons/weapon_flashbang.png',
    weapon_smokegrenade: '/img/icons/weapon_smokegrenade.png',
    smokegrenade: '/img/icons/weapon_smokegrenade.png',
    smoke: '/img/icons/weapon_smokegrenade.png',
    weapon_molotov: '/img/icons/weapon_molotov.png',
    molotov: '/img/icons/weapon_molotov.png',
    weapon_incgrenade: '/img/icons/weapon_incgrenade.png',
    incgrenade: '/img/icons/weapon_incgrenade.png',
    incendiary: '/img/icons/weapon_incgrenade.png',
    weapon_decoygrenade: '/img/icons/weapon_decoygrenade.png',
    decoy: '/img/icons/weapon_decoygrenade.png',
    weapon_decoy: '/img/icons/weapon_decoygrenade.png',
  };

  if (icons[typeLower]) {
    return icons[typeLower];
  }

  for (const [key, path] of Object.entries(icons)) {
    if (typeLower.includes(key) || key.includes(typeLower)) {
      return path;
    }
  }

  return null;
}

export function calculateNadeTrajectory(grenade, getConvertedCords) {
  if (!grenade || !getConvertedCords) return null;

  const trail = [];

  if (grenade.throwerX !== undefined && grenade.throwerY !== undefined) {
    const throwerCords = getConvertedCords(grenade.throwerX, grenade.throwerY);
    if (throwerCords) {
      trail.push(throwerCords);
    }
  }

  if (grenade.vectors && grenade.vectors.length > 0) {
    grenade.vectors.forEach((vector) => {
      if (vector.x !== undefined && vector.y !== undefined) {
        const cords = getConvertedCords(vector.x, vector.y);
        if (cords) {
          trail.push(cords);
        }
      }
    });
  }

  if (grenade.bounces && grenade.bounces.length > 0) {
    grenade.bounces.forEach((bounce) => {
      if (bounce.x !== undefined && bounce.y !== undefined) {
        const cords = getConvertedCords(bounce.x, bounce.y);
        if (cords) {
          trail.push(cords);
        }
      }
    });
  }

  if (grenade.grenadeX !== undefined && grenade.grenadeY !== undefined) {
    const finalCords = getConvertedCords(grenade.grenadeX, grenade.grenadeY);
    if (finalCords) {
      trail.push(finalCords);
    }
  }

  if (trail.length > 1) {
    const enhancedTrail = [trail[0]];
    for (let i = 1; i < trail.length; i++) {
      const arch = addArchToNade([], trail[i - 1], trail[i]);
      enhancedTrail.push(...arch.slice(1));
    }
    return enhancedTrail;
  }

  return trail;
}

export function updateNadeProjectiles(roundData, currentTick, currentFrame, frameTickMap, getConvertedCords, getObjectOpacity) {
  if (!roundData || !roundData.grenades) return [];

  const activeNades = [];

  roundData.grenades.forEach((grenade) => {
    const throwTick = grenade.throwTick || 0;
    const destroyTick = grenade.destroyTick || Infinity;
    const { effectStartTick } = grenade;
    const grenadeType = grenade.grenadeType?.toLowerCase() || '';
    const isSmoke = grenadeType.includes('smokegrenade') || grenadeType === 'smoke';

    let endTick = destroyTick;

    if (effectStartTick) {
      if (isSmoke) {
        endTick = effectStartTick;
      } else if (grenadeType.includes('flashbang') || grenadeType.includes('hegrenade')) {
        endTick = effectStartTick;
      }
    }

    if (isSmoke && effectStartTick && currentTick >= effectStartTick) {
      return;
    }

    if (currentTick >= throwTick && currentTick < endTick) {
      let currentX = grenade.grenadeX;
      let currentY = grenade.grenadeY;
      let currentZ = grenade.grenadeZ;

      if (grenade.vectors && grenade.vectors.length > 0) {
        const tickProgress = Math.max(0, Math.min(1, (currentTick - throwTick) / (endTick - throwTick)));
        const exactIndex = tickProgress * (grenade.vectors.length - 1);
        const vectorIndex = Math.floor(exactIndex);
        const nextIndex = Math.min(vectorIndex + 1, grenade.vectors.length - 1);
        const interpolationFactor = exactIndex - vectorIndex;

        if (vectorIndex < grenade.vectors.length) {
          const vector = grenade.vectors[vectorIndex];
          const nextVector = grenade.vectors[nextIndex];

          if (vector.x !== undefined && vector.y !== undefined) {
            if (nextVector && nextVector.x !== undefined && nextVector.y !== undefined && interpolationFactor > 0 && vectorIndex < grenade.vectors.length - 1) {
              currentX = vector.x + (nextVector.x - vector.x) * interpolationFactor;
              currentY = vector.y + (nextVector.y - vector.y) * interpolationFactor;
              currentZ = (vector.z !== undefined ? vector.z : grenade.grenadeZ || 0)
                        + ((nextVector.z !== undefined ? nextVector.z : grenade.grenadeZ || 0)
                         - (vector.z !== undefined ? vector.z : grenade.grenadeZ || 0)) * interpolationFactor;
            } else {
              currentX = vector.x;
              currentY = vector.y;
              currentZ = vector.z !== undefined ? vector.z : (grenade.grenadeZ || 0);
            }
          }
        }
      } else {
        currentZ = grenade.grenadeZ || 0;
      }

      const lastProcessedTick = Math.floor(currentTick / 16) * 16;
      const nextProcessedTick = lastProcessedTick + 16;
      const progress = (currentTick - lastProcessedTick) / 16;

      const cordsArr = [];
      let currentCords = null;
      let nextCords = null;
      const segments = [];
      let lastBounceTick = throwTick;

      if (grenade.bounces && grenade.bounces.length > 0) {
        grenade.bounces.forEach((bounce) => {
          segments.push(grenade.vectors.filter((vector) => {
            if (vector.tick !== undefined) {
              return vector.tick > lastBounceTick && vector.tick <= bounce.tick;
            }
            return false;
          }).filter((vector) => vector.x !== undefined && vector.y !== undefined));
          lastBounceTick = bounce.tick;
        });
      }

      segments.push(grenade.vectors.filter((vector) => {
        if (vector.tick !== undefined) {
          return vector.tick > lastBounceTick && vector.tick < destroyTick;
        }
        return false;
      }).filter((vector) => vector.x !== undefined && vector.y !== undefined));

      segments.forEach((segment) => {
        const segmentCoords = segment.map((v) => {
          const c = getConvertedCords(v.x, v.y);
          return c ? { x: c.x, y: c.y } : null;
        }).filter((c) => c !== null);

        if (segmentCoords.length < 2) return;

        const distance = calculateDistanceBetweenCoords(segmentCoords);

        segment.forEach((vector, index, self) => {
          let convertedCords = getConvertedCords(vector.x, vector.y);
          if (!convertedCords) return;

          convertedCords.z = vector.z;

          const completionPercentage = index / (self.length);
          convertedCords = addArchToNade(convertedCords, completionPercentage, distance);

          if (vector.tick === lastProcessedTick) {
            currentCords = convertedCords;
          }
          if (vector.tick === nextProcessedTick) {
            nextCords = convertedCords;
          }

          if (vector.tick !== undefined && vector.tick <= lastProcessedTick) {
            cordsArr.push(convertedCords.x, convertedCords.y);
          }
        });
      });

      if (!currentCords || cordsArr.length === 0) return;

      const interpolatedCords = nextCords ? {
        x: currentCords.x + (nextCords.x - currentCords.x) * progress,
        y: currentCords.y + (nextCords.y - currentCords.y) * progress,
        z: currentCords.z + (nextCords.z - currentCords.z) * progress,
      } : currentCords;

      const enhancedTrail = [];
      for (let i = 0; i < cordsArr.length; i += 2) {
        if (i + 1 < cordsArr.length) {
          enhancedTrail.push({ x: cordsArr[i], y: cordsArr[i + 1] });
        }
      }
      enhancedTrail.push({ x: interpolatedCords.x, y: interpolatedCords.y });

      const opacity = getObjectOpacity ? getObjectOpacity(interpolatedCords.z, 1) : 1;
      const iconPath = getGrenadeIcon(grenade.grenadeType);
      const throwerSide = grenade.throwerSide || 'CT';
      const teamColor = throwerSide === 'CT' ? '#687cca' : '#e38617';

      const stableZ = Math.round(interpolatedCords.z * 10) / 10;

      let heightScale = 1.0;
      const throwZ = grenade.throwerZ !== undefined ? grenade.throwerZ
        : (grenade.vectors && grenade.vectors.length > 0 && grenade.vectors[0].z !== undefined ? grenade.vectors[0].z : 0);

      const nadeZ = interpolatedCords.z;
      const heightAboveThrow = nadeZ - throwZ;

      if (heightAboveThrow > 0) {
        const maxHeight = grenade.vectors && grenade.vectors.length > 0
          ? Math.max(...grenade.vectors.map((v) => (v.z !== undefined ? v.z : throwZ)).filter((z) => z !== undefined)) : throwZ;
        const maxHeightAboveThrow = Math.max(0, maxHeight - throwZ);

        if (maxHeightAboveThrow > 0) {
          const normalizedHeight = heightAboveThrow / maxHeightAboveThrow;
          heightScale = 1.0 + (normalizedHeight * 0.8);
        }
      }

      activeNades.push({
        uniqueID: grenade.uniqueID,
        x: interpolatedCords.x,
        y: interpolatedCords.y,
        z: stableZ,
        color: teamColor,
        icon: iconPath,
        trail: enhancedTrail,
        opacity,
        heightScale,
        grenadeType: grenade.grenadeType || 'hegrenade',
      });
    }
  });

  return activeNades;
}
